// src/pages/tips/TipsIndex.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ImgVegetables, ImgPantry, ImgBatch, ImgSubstitutions, ImgFoodWaste, ImgKnife, ImgSeasoning, ImgProtein, ImgMealPlan, ImgBudget } from './images';

export const ARTICLES = [
  {
    slug: 'use-up-vegetables',
    Img: ImgVegetables,
    title: 'How to Use Up Vegetables Before They Go Bad',
    description: 'Simple strategies to turn wilting veg into delicious meals - and stop throwing money in the bin.',
    emoji: '🥦',
    readTime: '5 min read',
    date: 'March 2026',
  },
  {
    slug: 'pantry-staples-guide',
    Img: ImgPantry,
    title: 'The 12 Pantry Staples That Make Any Meal Possible',
    description: 'Keep these ingredients stocked and you\'ll never stare blankly at your fridge again.',
    emoji: '🧂',
    readTime: '6 min read',
    date: 'March 2026',
  },
  {
    slug: 'batch-cooking-guide',
    Img: ImgBatch,
    title: 'Batch Cooking 101: Cook Once, Eat All Week',
    description: 'How to spend 2 hours on Sunday and have great meals ready every night of the week.',
    emoji: '🍲',
    readTime: '7 min read',
    date: 'March 2026',
  },
  {
    slug: 'ingredient-substitutions',
    Img: ImgSubstitutions,
    title: 'Smart Ingredient Substitutions Every Home Cook Should Know',
    description: 'Out of an ingredient? Here\'s what to swap in without ruining the dish.',
    emoji: '🔄',
    readTime: '6 min read',
    date: 'March 2026',
  },
  {
    slug: 'knife-skills-guide',
    Img: ImgKnife,
    title: 'Knife Skills: The Complete Beginner\'s Guide',
    description: 'Learn the cuts, grips, and techniques that make cooking faster, safer, and more enjoyable.',
    emoji: '🔪',
    readTime: '8 min read',
    date: 'April 2026',
  },
  {
    slug: 'seasoning-guide',
    Img: ImgSeasoning,
    title: 'How to Season Food Properly: The Complete Guide',
    description: 'Why your home cooking doesn\'t taste like restaurant food, and exactly how to fix it.',
    emoji: '🧂',
    readTime: '7 min read',
    date: 'April 2026',
  },
  {
    slug: 'protein-cooking-guide',
    Img: ImgProtein,
    title: 'How to Cook Chicken, Beef, and Fish Perfectly Every Time',
    description: 'Internal temperatures, resting times, and the techniques that guarantee juicy, properly cooked protein.',
    emoji: '🍗',
    readTime: '9 min read',
    date: 'April 2026',
  },
  {
    slug: 'meal-planning-guide',
    Img: ImgMealPlan,
    title: 'Meal Planning for Beginners: A Realistic Step-by-Step Guide',
    description: 'How to plan a week of meals without spending hours on it or burning out after two weeks.',
    emoji: '🗓️',
    readTime: '7 min read',
    date: 'April 2026',
  },
  {
    slug: 'budget-cooking-guide',
    Img: ImgBudget,
    title: 'Eating Well on a Budget: 15 Strategies That Actually Work',
    description: 'How to cook nutritious, satisfying meals without spending a fortune.',
    emoji: '💰',
    readTime: '8 min read',
    date: 'April 2026',
  },
  {
    slug: 'reduce-food-waste',
    Img: ImgFoodWaste,
    title: '10 Ways to Reduce Food Waste in Your Kitchen',
    description: 'Practical habits that save money, help the planet, and make you a better cook.',
    emoji: '🌱',
    readTime: '5 min read',
    date: 'March 2026',
  },
];

export default function TipsIndex() {
  useEffect(() => {
    document.title = 'Cooking Tips & Guides — OhAPotato';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = 'Practical cooking guides to help you cook better meals, waste less food, and make the most of whatever\'s in your kitchen.';
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://ohapotato.com/tips';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <Link to="/" style={s.navHome}>OhAPotato</Link>
        <span style={s.navSep}>›</span>
        <span style={s.navCurrent}>Cooking Tips</span>
      </nav>
      <main style={s.main}>
        <header style={s.hero}>
          <p style={s.eyebrow}>Cooking Guides</p>
          <h1 style={s.title}>Tips for cooking smarter</h1>
          <p style={s.sub}>
            Practical guides to help you cook better meals, waste less food,
            and make the most of whatever's in your kitchen.
          </p>
        </header>

        <div style={s.grid}>
          {ARTICLES.map(a => (
            <Link to={`/tips/${a.slug}`} key={a.slug} style={s.card}
              onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent-orange)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-base)'; e.currentTarget.style.boxShadow='var(--shadow-sm)'; }}
            >
              {a.Img && <div style={s.cardImg}><a.Img /></div>}
              <div style={s.cardBody}>
                <h2 style={s.cardTitle}>{a.title}</h2>
                <p style={s.cardDesc}>{a.description}</p>
                <div style={s.cardMeta}>
                  <span style={s.metaItem}>{a.readTime}</span>
                  <span style={s.metaDot}>·</span>
                  <span style={s.metaItem}>{a.date}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/about" style={s.footerLink}>About</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/privacy" style={s.footerLink}>Privacy</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/contact" style={s.footerLink}>Contact</Link>
        </footer>
      </main>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'var(--bg-base)', color:'var(--text-primary)' },
  nav:  { padding:'14px 32px', borderBottom:'1px solid var(--border-base)', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' },
  navHome: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--accent-orange)', textDecoration:'none', fontWeight:'700' },
  navSep: { fontSize:'12px', color:'var(--text-muted)', opacity:0.6 },
  navCurrent: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-muted)' },
  main: { maxWidth:'760px', margin:'0 auto', padding:'48px 24px 80px' },
  hero: { marginBottom:'48px' },
  eyebrow: { fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:'700', letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--accent-orange)', marginBottom:'12px' },
  title: { fontFamily:'var(--font-display)', fontSize:'clamp(28px,5vw,40px)', fontWeight:'700', lineHeight:'1.15', marginBottom:'14px', letterSpacing:'-0.5px' },
  sub: { fontFamily:'var(--font-body)', fontSize:'16px', color:'var(--text-secondary)', lineHeight:'1.7', maxWidth:'520px' },
  grid: { display:'flex', flexDirection:'column', gap:'16px', marginBottom:'64px' },
  card: {
    display:'flex', flexDirection:'column', gap:'0',
    background:'var(--bg-card)', border:'1px solid var(--border-base)',
    borderRadius:'var(--radius-lg)', overflow:'hidden',
    textDecoration:'none', color:'inherit',
    boxShadow:'var(--shadow-sm)',
    transition:'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  cardEmoji: { fontSize:'36px', flexShrink:0, lineHeight:1, marginTop:'2px' },
  cardImg: { width:'100%', borderRadius:'var(--radius-md) var(--radius-md) 0 0', overflow:'hidden', marginBottom:'0', flexShrink:0 },
  cardBody: { flex:1, padding:'18px 20px 20px' },
  cardTitle: { fontFamily:'var(--font-display)', fontSize:'18px', fontWeight:'600', color:'var(--text-primary)', marginBottom:'8px', lineHeight:'1.3', letterSpacing:'-0.2px' },
  cardDesc: { fontFamily:'var(--font-body)', fontSize:'14px', color:'var(--text-secondary)', lineHeight:'1.65', marginBottom:'12px' },
  cardMeta: { display:'flex', alignItems:'center', gap:'6px' },
  metaItem: { fontFamily:'var(--font-body)', fontSize:'12px', color:'var(--text-muted)' },
  metaDot: { fontSize:'10px', color:'var(--text-muted)', opacity:0.5 },
  footer: { paddingTop:'24px', borderTop:'1px solid var(--border-base)', display:'flex', gap:'12px', alignItems:'center', justifyContent:'center' },
  footerLink: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-muted)', textDecoration:'none' },
  footerDot: { color:'var(--text-muted)', fontSize:'11px' },
};
